import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSort, type SortConfig } from '../../../hooks/useSort';
import { useSettingsStore } from '../../../store/settingsStore';
import { usePermissions } from '../../../hooks/usePermissions';
import { reportService, type BusinessReportResponse, type CustomerPurchaseDetailResponse, type CustomerReportResponse, type FinishedGoodsReportResponse, type FinishedGoodsReportRow, type GreenBeansReportResponse, type GreenBeansReportRow, type InventoryItemType, type ReportMode, type RoastedBeansReportResponse, type RoastedBeansReportRow, type RoastingReportResponse, type RoastingReportRow } from '../../../services/report.service';
import { workOrderService } from '../../../services/workOrderService';
import { inventoryService } from '../../../services/inventoryService';
import { salesOrderService } from '../../../services/salesOrderService';

export type DateRange = 'thisWeek' | 'thisMonth' | 'lastMonth' | 'thisQuarter' | 'custom';

const toDateInput = (date: Date) => {
  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60 * 1000);
  return localDate.toISOString().split('T')[0];
};

const getRangeDates = (range: Exclude<DateRange, 'custom'>) => {
  const now = new Date();
  let start = new Date();
  let end = new Date();

  if (range === 'thisWeek') {
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    start = new Date(now.getFullYear(), now.getMonth(), diff);
    end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
  } else if (range === 'thisMonth') {
    start = new Date(now.getFullYear(), now.getMonth(), 1);
    end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  } else if (range === 'lastMonth') {
    start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    end = new Date(now.getFullYear(), now.getMonth(), 0);
  } else {
    const quarter = Math.floor(now.getMonth() / 3);
    start = new Date(now.getFullYear(), quarter * 3, 1);
    end = new Date(now.getFullYear(), quarter * 3 + 3, 0);
  }

  return { start: toDateInput(start), end: toDateInput(end) };
};

const inRange = (value: string | undefined, start: string, end: string) => {
  if (!value) return false;
  const target = new Date(value).getTime();
  if (Number.isNaN(target)) return false;
  const min = new Date(start).getTime();
  const max = new Date(end).getTime();
  return target >= min && target <= max;
};

const getErrorMessage = (error: unknown) => {
  if (error && typeof error === 'object') {
    const e = error as Record<string, unknown>;
    const response = e.response as Record<string, unknown> | undefined;
    const responseData = response?.data as Record<string, unknown> | undefined;
    const responseError = responseData?.error;
    if (typeof responseError === 'string') {
      return responseError;
    }
  }
  return (error as Error)?.message || 'Failed to fetch report data';
};

const emptyRoasting = (): RoastingReportResponse => ({
  summary: { totalBatches: 0, totalInputWeight: 0, totalOutputWeight: 0, totalWeightLoss: 0, averageLossPercent: 0 },
  rows: [],
});

const emptyGreenBeans = (): GreenBeansReportResponse => ({
  summary: { totalItems: 0, totalRemainingQuantity: 0, totalStockValue: 0 },
  rows: [],
});

const emptyRoastedBeans = (): RoastedBeansReportResponse => ({
  summary: { totalItems: 0, totalRemainingQuantity: 0, totalStockValue: 0 },
  rows: [],
});

const emptyFinishedGoods = (): FinishedGoodsReportResponse => ({
  summary: { totalItems: 0, totalRemainingQuantity: 0, totalStockValue: 0 },
  rows: [],
});

const emptyBusiness = (): BusinessReportResponse => ({
  summary: { totalRevenue: 0, totalOrders: 0, activeCustomers: 0, averageOrderValue: 0, netProfit: 0 },
  customerBreakdown: [],
  purchaseHistory: [],
  revenueTrend: [],
});

const emptyCustomerList = (): CustomerReportResponse => ({ customers: [] });

export const useReportPageState = () => {
  const { currencySymbol } = useSettingsStore();
  const initialDates = useMemo(() => getRangeDates('thisMonth'), []);
  const { can } = usePermissions();

  // Spec 100: each report section is its own permission. Initialise mode to
  // the first one the user actually has access to so we don't immediately
  // 403 the data fetch.
  const initialMode = useMemo<ReportMode>(() => {
    const order: Array<{ mode: ReportMode; perm: string }> = [
      { mode: 'roasting', perm: 'reports.roasting' },
      { mode: 'greenBeans', perm: 'reports.green_beans' },
      { mode: 'roastedBeans', perm: 'reports.roasted_beans' },
      { mode: 'finishedGoods', perm: 'reports.finished_goods' },
      { mode: 'business', perm: 'reports.business' },
      { mode: 'customer', perm: 'reports.customer' },
    ];
    const first = order.find((m) => can(m.perm, 'read'));
    return first ? first.mode : 'roasting';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [mode, setMode] = useState<ReportMode>(initialMode);
  const [inventoryEntryMode, setInventoryEntryMode] = useState<ReportMode>(
    initialMode === 'roastedBeans' || initialMode === 'finishedGoods' ? initialMode : 'greenBeans'
  );
  const [businessEntryMode, setBusinessEntryMode] = useState<ReportMode>(
    initialMode === 'customer' ? 'customer' : 'business'
  );
  const [dateRange, setDateRange] = useState<DateRange>('thisMonth');
  const [startDate, setStartDate] = useState(initialDates.start);
  const [endDate, setEndDate] = useState(initialDates.end);
  const [selectedTrackingItem, setSelectedTrackingItem] = useState<{
    itemId: string;
    itemType: InventoryItemType;
  } | null>(null);
  const [loadingByMode, setLoadingByMode] = useState<Record<ReportMode, boolean>>({
    roasting: false,
    greenBeans: false,
    roastedBeans: false,
    finishedGoods: false,
    business: false,
    customer: false,
  });
  const [errorByMode, setErrorByMode] = useState<Record<ReportMode, string | null>>({
    roasting: null,
    greenBeans: null,
    roastedBeans: null,
    finishedGoods: null,
    business: null,
    customer: null,
  });
  const [roastingReport, setRoastingReport] = useState<RoastingReportResponse>(emptyRoasting);
  const [greenBeansReport, setGreenBeansReport] = useState<GreenBeansReportResponse>(emptyGreenBeans);
  const [roastedBeansReport, setRoastedBeansReport] = useState<RoastedBeansReportResponse>(emptyRoastedBeans);
  const [finishedGoodsReport, setFinishedGoodsReport] = useState<FinishedGoodsReportResponse>(emptyFinishedGoods);
  const [businessReport, setBusinessReport] = useState<BusinessReportResponse>(emptyBusiness);
  const [customerListReport, setCustomerListReport] = useState<CustomerReportResponse>(emptyCustomerList);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [customerDetail, setCustomerDetail] = useState<CustomerPurchaseDetailResponse | null>(null);
  const [customerDetailLoading, setCustomerDetailLoading] = useState(false);
  const [customerDetailError, setCustomerDetailError] = useState<string | null>(null);

  const setModeLoading = useCallback((targetMode: ReportMode, value: boolean) => {
    setLoadingByMode(prev => ({ ...prev, [targetMode]: value }));
  }, []);

  const setModeError = useCallback((targetMode: ReportMode, value: string | null) => {
    setErrorByMode(prev => ({ ...prev, [targetMode]: value }));
  }, []);

  const buildRoastingFallback = useCallback(async () => {
    const workOrders = await workOrderService.getAll();
    const rows: RoastingReportRow[] = workOrders
      .filter(item => inRange(item.date || item.startDate, startDate, endDate))
      .map(item => {
        const inputWeight = item.inputWeight ?? item.roastProfile?.weightAnalysis?.inputWeight ?? 0;
        const outputWeight = item.outputWeight ?? item.roastProfile?.weightAnalysis?.outputWeight ?? 0;
        const weightLossKg = Math.max(inputWeight - outputWeight, 0);
        const weightLossPercent = inputWeight > 0 ? (weightLossKg / inputWeight) * 100 : 0;
        return {
          id: item.id,
          roastingDate: item.date || item.startDate || '',
          batchNumber: item.workOrderNumber || '-',
          greenBeanCode: item.itemCode || '-',
          greenBeanName: item.itemName || item.recipeName || '-',
          inputWeight,
          outputWeight,
          weightLossKg,
          weightLossPercent,
          roasterName: item.roaster,
          machineName: item.machine,
        };
      });
    const totalInputWeight = rows.reduce((acc, row) => acc + row.inputWeight, 0);
    const totalOutputWeight = rows.reduce((acc, row) => acc + row.outputWeight, 0);
    const totalWeightLoss = rows.reduce((acc, row) => acc + row.weightLossKg, 0);
    const averageLossPercent = rows.length > 0 ? rows.reduce((acc, row) => acc + row.weightLossPercent, 0) / rows.length : 0;
    return {
      summary: {
        totalBatches: rows.length,
        totalInputWeight,
        totalOutputWeight,
        totalWeightLoss,
        averageLossPercent,
      },
      rows,
    };
  }, [endDate, startDate]);

  const buildGreenBeansFallback = useCallback(async () => {
    const lotsData = await inventoryService.getAllLots({
      item_type: 'green_bean',
      status: 'active',
      order_by: 'remaining_quantity',
      order_dir: 'desc',
      limit: 1000,
      start_date: startDate,
      end_date: endDate,
    });
    const map = new Map<string, GreenBeansReportRow>();
    lotsData.data.forEach(lot => {
      const existing = map.get(lot.inventoryItemId);
      const nextQuantity = (existing?.remainingQuantity || 0) + (lot.remainingQuantity || 0);
      const nextCost = (existing?.averageCost || 0) + ((lot.unitPrice || 0) * (lot.remainingQuantity || 0));
      const costBase = nextQuantity || 1;
      map.set(lot.inventoryItemId, {
        itemId: lot.inventoryItemId,
        itemCode: lot.itemCode || existing?.itemCode || '-',
        itemName: lot.itemName || existing?.itemName || '-',
        remainingQuantity: nextQuantity,
        unitOfMeasure: 'kg',
        averageCost: nextCost / costBase,
        supplierName: lot.supplierName || existing?.supplierName || '-',
        lastMovementDate: !existing?.lastMovementDate || (lot.receivedDate && lot.receivedDate > existing.lastMovementDate)
          ? lot.receivedDate
          : existing.lastMovementDate,
      });
    });
    const rows = Array.from(map.values()).sort((a, b) => b.remainingQuantity - a.remainingQuantity);
    return {
      summary: {
        totalItems: rows.length,
        totalRemainingQuantity: rows.reduce((acc, row) => acc + row.remainingQuantity, 0),
        totalStockValue: rows.reduce((acc, row) => acc + row.averageCost * row.remainingQuantity, 0),
      },
      rows,
    };
  }, [endDate, startDate]);

  const buildRoastedBeansFallback = useCallback(async () => {
    const groups = await inventoryService.getRoastedBulkGroups(true);
    const rows: RoastedBeansReportRow[] = groups.map(group => ({
      itemId: group.itemId,
      itemCode: group.itemCode || '-',
      itemName: group.itemName || '-',
      remainingQuantity: group.totalStock || 0,
      unitOfMeasure: 'kg',
      averageCost: group.averagePrice || 0,
      relatedBatch: group.items?.[0]?.workOrderNumber || '-',
      lastProductionDate: undefined,
    }));
    rows.sort((a, b) => b.remainingQuantity - a.remainingQuantity);
    return {
      summary: {
        totalItems: rows.length,
        totalRemainingQuantity: rows.reduce((acc, row) => acc + row.remainingQuantity, 0),
        totalStockValue: rows.reduce((acc, row) => acc + row.averageCost * row.remainingQuantity, 0),
      },
      rows,
    };
  }, []);

  const buildFinishedGoodsFallback = useCallback(async () => {
    const items = await inventoryService.getFinishedGoodsItems();
    const rows: FinishedGoodsReportRow[] = items.map(item => ({
      itemId: item.id,
      itemCode: item.code || '-',
      itemName: item.name || '-',
      remainingQuantity: item.stock || 0,
      unitOfMeasure: item.unitOfMeasure || 'pcs',
      averageCost: item.averagePrice ?? item.standardCost ?? 0,
      lastProductionDate: undefined,
    }));
    rows.sort((a, b) => b.remainingQuantity - a.remainingQuantity);
    return {
      summary: {
        totalItems: rows.length,
        totalRemainingQuantity: rows.reduce((acc, row) => acc + row.remainingQuantity, 0),
        totalStockValue: rows.reduce((acc, row) => acc + row.averageCost * row.remainingQuantity, 0),
      },
      rows,
    };
  }, []);

  const buildBusinessFallback = useCallback(async () => {
    const orders = await salesOrderService.getAll();
    const filtered = orders.filter(order => inRange(order.orderDate, startDate, endDate));
    const customerMap = new Map<string, { customerId?: string; customerName: string; totalRevenue: number; orderCount: number; lastOrderDate?: string }>();
    filtered.forEach(order => {
      const key = order.customer || 'Unknown';
      const current = customerMap.get(key) || {
        customerId: undefined,
        customerName: order.customer || 'Unknown',
        totalRevenue: 0,
        orderCount: 0,
        lastOrderDate: undefined,
      };
      current.totalRevenue += order.totalAmount || 0;
      current.orderCount += 1;
      if (!current.lastOrderDate || (order.orderDate && order.orderDate > current.lastOrderDate)) {
        current.lastOrderDate = order.orderDate;
      }
      customerMap.set(key, current);
    });

    const revenueByMonth = new Map<string, number>();
    filtered.forEach(order => {
      const orderDate = order.orderDate || '';
      const date = new Date(orderDate);
      if (Number.isNaN(date.getTime())) return;
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      revenueByMonth.set(key, (revenueByMonth.get(key) || 0) + (order.totalAmount || 0));
    });

    const totalRevenue = filtered.reduce((acc, order) => acc + (order.totalAmount || 0), 0);
    return {
      summary: {
        totalRevenue,
        totalOrders: filtered.length,
        activeCustomers: customerMap.size,
        averageOrderValue: filtered.length > 0 ? totalRevenue / filtered.length : 0,
        netProfit: 0,
      },
      customerBreakdown: Array.from(customerMap.values()).sort((a, b) => b.totalRevenue - a.totalRevenue),
      purchaseHistory: filtered
        .slice()
        .sort((a, b) => (b.orderDate || '').localeCompare(a.orderDate || ''))
        .map(order => ({
          orderId: order.id,
          orderNumber: order.orderNumber || '-',
          orderDate: order.orderDate || '',
          customerName: order.customer || 'Unknown',
          totalAmount: order.totalAmount || 0,
          status: order.status || 'Draft',
        })),
      revenueTrend: Array.from(revenueByMonth.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, revenue]) => ({
          label: key,
          periodStart: `${key}-01`,
          revenue,
        })),
    };
  }, [endDate, startDate]);

  const loadCurrentMode = useCallback(async () => {
    setModeLoading(mode, true);
    setModeError(mode, null);
    try {
      if (mode === 'roasting') {
        const data = await reportService.getRoastingReport({ startDate, endDate });
        setRoastingReport(data);
      } else if (mode === 'greenBeans') {
        const data = await reportService.getGreenBeansReport({ startDate, endDate });
        setGreenBeansReport(data);
      } else if (mode === 'roastedBeans') {
        const data = await reportService.getRoastedBeansReport({ startDate, endDate });
        setRoastedBeansReport(data);
      } else if (mode === 'finishedGoods') {
        const data = await reportService.getFinishedGoodsReport({ startDate, endDate });
        setFinishedGoodsReport(data);
      } else if (mode === 'business') {
        const data = await reportService.getBusinessReport({ startDate, endDate });
        setBusinessReport(data);
      } else if (mode === 'customer') {
        const data = await reportService.getCustomerReport({ startDate, endDate });
        setCustomerListReport(data);
      }
    } catch (error) {
      if (mode === 'roasting') {
        const fallback = await buildRoastingFallback();
        setRoastingReport(fallback);
      } else if (mode === 'greenBeans') {
        const fallback = await buildGreenBeansFallback();
        setGreenBeansReport(fallback);
      } else if (mode === 'roastedBeans') {
        const fallback = await buildRoastedBeansFallback();
        setRoastedBeansReport(fallback);
      } else if (mode === 'finishedGoods') {
        const fallback = await buildFinishedGoodsFallback();
        setFinishedGoodsReport(fallback);
      } else if (mode === 'business') {
        const fallback = await buildBusinessFallback();
        setBusinessReport(fallback);
      } else if (mode === 'customer') {
        // New endpoint — no client-side fallback; surface empty + error note.
        setCustomerListReport(emptyCustomerList());
      }
      setModeError(mode, `Live endpoint unavailable: ${getErrorMessage(error)}. Displaying fallback data.`);
    } finally {
      setModeLoading(mode, false);
    }
  }, [buildBusinessFallback, buildFinishedGoodsFallback, buildGreenBeansFallback, buildRoastedBeansFallback, buildRoastingFallback, endDate, mode, setModeError, setModeLoading, startDate]);

  useEffect(() => {
    loadCurrentMode();
  }, [loadCurrentMode]);

  useEffect(() => {
    if (mode !== 'customer' || !selectedCustomerId) return;
    let cancelled = false;
    setCustomerDetailLoading(true);
    setCustomerDetailError(null);
    reportService
      .getCustomerPurchaseDetail(selectedCustomerId, { startDate, endDate })
      .then(detail => {
        if (!cancelled) setCustomerDetail(detail);
      })
      .catch(error => {
        if (!cancelled) {
          setCustomerDetail(null);
          setCustomerDetailError(getErrorMessage(error));
        }
      })
      .finally(() => {
        if (!cancelled) setCustomerDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, selectedCustomerId, startDate, endDate]);

  const handleModeChange = (nextMode: ReportMode) => {
    if (nextMode === 'greenBeans' || nextMode === 'roastedBeans' || nextMode === 'finishedGoods') {
      setInventoryEntryMode(nextMode);
    }
    if (nextMode === 'business' || nextMode === 'customer') {
      setBusinessEntryMode(nextMode);
    }
    setMode(nextMode);
  };

  const openTracking = useCallback((itemId: string, itemType: InventoryItemType) => {
    setSelectedTrackingItem({ itemId, itemType });
  }, []);

  const closeTracking = useCallback(() => {
    setSelectedTrackingItem(null);
  }, []);

  const selectCustomer = useCallback((customerId: string) => {
    setSelectedCustomerId(customerId);
  }, []);

  const clearSelectedCustomer = useCallback(() => {
    setSelectedCustomerId(null);
    setCustomerDetail(null);
    setCustomerDetailError(null);
  }, []);

  const handleRangeChange = (range: DateRange) => {
    setDateRange(range);
    if (range === 'custom') return;
    const nextDates = getRangeDates(range);
    setStartDate(nextDates.start);
    setEndDate(nextDates.end);
  };

  const handleStartDateChange = (value: string) => {
    setDateRange('custom');
    setStartDate(value);
  };

  const handleEndDateChange = (value: string) => {
    setDateRange('custom');
    setEndDate(value);
  };

  const roastingSort = useSort<RoastingReportRow>(roastingReport.rows, {
    key: 'roastingDate',
    direction: 'desc',
  } as SortConfig<RoastingReportRow>);
  const greenBeansSort = useSort<GreenBeansReportRow>(greenBeansReport.rows, {
    key: 'remainingQuantity',
    direction: 'desc',
  } as SortConfig<GreenBeansReportRow>);
  const roastedBeansSort = useSort<RoastedBeansReportRow>(roastedBeansReport.rows, {
    key: 'remainingQuantity',
    direction: 'desc',
  } as SortConfig<RoastedBeansReportRow>);
  const finishedGoodsSort = useSort<FinishedGoodsReportRow>(finishedGoodsReport.rows, {
    key: 'remainingQuantity',
    direction: 'desc',
  } as SortConfig<FinishedGoodsReportRow>);

  return {
    currencySymbol,
    mode,
    inventoryEntryMode,
    dateRange,
    startDate,
    endDate,
    handleModeChange,
    handleRangeChange,
    handleStartDateChange,
    handleEndDateChange,
    loadingByMode,
    errorByMode,
    roastingReport,
    greenBeansReport,
    roastedBeansReport,
    finishedGoodsReport,
    businessReport,
    businessEntryMode,
    customerListReport,
    selectedCustomerId,
    selectCustomer,
    clearSelectedCustomer,
    customerDetail,
    customerDetailLoading,
    customerDetailError,
    selectedTrackingItem,
    openTracking,
    closeTracking,
    roastingRows: roastingSort.sortedItems,
    roastingSortConfig: roastingSort.sortConfig as SortConfig<RoastingReportRow> | null,
    sortRoastingRows: roastingSort.handleSort,
    greenBeansRows: greenBeansSort.sortedItems,
    greenBeansSortConfig: greenBeansSort.sortConfig as SortConfig<GreenBeansReportRow> | null,
    sortGreenBeansRows: greenBeansSort.handleSort,
    roastedBeansRows: roastedBeansSort.sortedItems,
    roastedBeansSortConfig: roastedBeansSort.sortConfig as SortConfig<RoastedBeansReportRow> | null,
    sortRoastedBeansRows: roastedBeansSort.handleSort,
    finishedGoodsRows: finishedGoodsSort.sortedItems,
    finishedGoodsSortConfig: finishedGoodsSort.sortConfig as SortConfig<FinishedGoodsReportRow> | null,
    sortFinishedGoodsRows: finishedGoodsSort.handleSort,
  };
};
